// Pure geometry for the faction-tracking edge arrows. Kept free of PIXI so it can be
// unit-tested. All inputs are in ONE consistent coordinate space (the overlay container's
// local space); the caller obtains points/rect via PIXI transforms (toLocal) so no screen
// dimensions are assumed here.

export interface StagePoint {
    x: number
    y: number
}

export interface Rect {
    left: number
    top: number
    right: number
    bottom: number
}

export interface EdgePlacement {
    x: number
    y: number
    angle: number // radians, from rect center toward the target
    // Which screen edge the arrow is pinned to: true = a left/right (vertical) edge, false =
    // a top/bottom (horizontal) edge. Lets the label/bubble offset perpendicular to that edge
    // (and stay aligned along it) instead of along the tilted ray.
    onVerticalEdge: boolean
}

// Build an axis-aligned rect from two opposite corners (order-independent).
export function normalizeRect(a: StagePoint, b: StagePoint): Rect {
    return {
        left: Math.min(a.x, b.x),
        right: Math.max(a.x, b.x),
        top: Math.min(a.y, b.y),
        bottom: Math.max(a.y, b.y),
    }
}

// True if the point is outside the rect (optionally shrunk by pad on all sides).
export function isOffscreen(p: StagePoint, rect: Rect, pad: number = 0): boolean {
    return p.x < rect.left + pad || p.x > rect.right - pad || p.y < rect.top + pad || p.y > rect.bottom - pad
}

// Clamp a box of size (w, h) centered on (x, y) so it stays fully inside `rect` (with
// optional padding). Keeps arrow bubbles/labels from overflowing off the screen edges.
// If the box is larger than the rect on an axis, it is centered on that axis.
export function clampBoxInside(x: number, y: number, w: number, h: number, rect: Rect, pad: number = 2): StagePoint {
    const halfW = w / 2 + pad
    const halfH = h / 2 + pad
    const minX = rect.left + halfW
    const maxX = rect.right - halfW
    const minY = rect.top + halfH
    const maxY = rect.bottom - halfH
    return {
        x: minX > maxX ? (rect.left + rect.right) / 2 : Math.max(minX, Math.min(maxX, x)),
        y: minY > maxY ? (rect.top + rect.bottom) / 2 : Math.max(minY, Math.min(maxY, y)),
    }
}

// Place an axis-aligned box of size (w, h) next to an edge arrow so it never overlaps the
// arrow AND stays visually tied to it. `placement` is the arrow point + which edge it sits
// on (from clampToRectEdge); `arrowClear` is how far the arrow reaches toward the box; `gap`
// is the extra clear space between the arrow and the box.
//
// The box is offset PERPENDICULAR to the arrow's screen edge and kept ALIGNED along it:
//   - arrow on a left/right edge  -> box sits inward horizontally, centered on the arrow's y
//   - arrow on a top/bottom edge  -> box sits inward vertically,   centered on the arrow's x
// This is what makes a horizontal "< Name" read as vertically centered on the arrow (the old
// along-the-ray placement tilted the box off-center whenever the ray wasn't axis-aligned) and
// keeps the box a constant, close distance on diagonals instead of flinging it far inward. The
// box's near edge always clears the arrow by exactly (arrowClear + gap). Finally it's clamped
// on screen (which only pushes further inward, so it can't reintroduce overlap).
export function placeInwardBox(
    placement: EdgePlacement,
    w: number,
    h: number,
    rect: Rect,
    arrowClear: number,
    gap: number,
): StagePoint {
    return offsetInwardBox(placement, placement.x, placement.y, arrowClear + gap, w, h, rect)
}

// Place a box of (w, h) just PAST an already-placed anchor box (e.g. a hover panel inward of
// the role glyph): same edge/direction, aligned along the edge, its near edge `gap` beyond the
// anchor's far edge. `anchor` is the anchor box's center; anchorW/anchorH its size.
export function placeInwardOfBox(
    placement: EdgePlacement,
    anchor: StagePoint,
    anchorW: number,
    anchorH: number,
    w: number,
    h: number,
    rect: Rect,
    gap: number,
): StagePoint {
    const anchorHalf = placement.onVerticalEdge ? anchorW / 2 : anchorH / 2
    return offsetInwardBox(placement, anchor.x, anchor.y, anchorHalf + gap, w, h, rect)
}

// Shared core: offset a box inward (perpendicular to the placement's screen edge), aligned
// with the anchor point on the along-edge axis, so the box's near edge sits `clearance` past
// the anchor. Then clamp it on screen (which only pushes further inward -> no re-overlap).
function offsetInwardBox(
    placement: EdgePlacement,
    ax: number,
    ay: number,
    clearance: number,
    w: number,
    h: number,
    rect: Rect,
): StagePoint {
    let x = ax
    let y = ay
    if (placement.onVerticalEdge) {
        // sign(cos) matches sign(anchor.x - center.x): +1 on the right edge, -1 on the left.
        // Subtracting pushes the box inward by half its width plus the clearance.
        x = ax - Math.sign(Math.cos(placement.angle)) * (clearance + w / 2)
    } else {
        y = ay - Math.sign(Math.sin(placement.angle)) * (clearance + h / 2)
    }
    return clampBoxInside(x, y, w, h, rect)
}

// Project a target point onto the rect's edge along the ray from the rect center, inset by
// `margin`. As the target moves around outside the rect, the returned point glides along the
// rect edges (and around corners). Also reports which edge (vertical vs horizontal) the ray
// hit first, so callers can place a label perpendicular to it.
export function clampToRectEdge(target: StagePoint, rect: Rect, margin: number): EdgePlacement {
    const cx = (rect.left + rect.right) / 2
    const cy = (rect.top + rect.bottom) / 2
    const dx = target.x - cx
    const dy = target.y - cy

    if (dx === 0 && dy === 0) {
        return { x: cx, y: cy, angle: 0, onVerticalEdge: true }
    }

    const halfW = Math.max(1, (rect.right - rect.left) / 2 - margin)
    const halfH = Math.max(1, (rect.bottom - rect.top) / 2 - margin)

    // Whichever normalized reach is smaller binds first: the x-scale binding means the ray
    // exits through a left/right (vertical) edge; the y-scale, a top/bottom (horizontal) one.
    const sx = halfW / Math.abs(dx || 1e-9)
    const sy = halfH / Math.abs(dy || 1e-9)
    const scale = Math.min(sx, sy)

    return {
        x: cx + dx * scale,
        y: cy + dy * scale,
        angle: Math.atan2(dy, dx),
        onVerticalEdge: sx <= sy,
    }
}
